import { ReactNode, useEffect, useState } from "react"
import MobileAlternative from "./MobileAlternative"
import notNull from "../util/notNull"

type IDENTIFIABLE = { id: string }

interface TableProps<TYPE extends IDENTIFIABLE> {
    header: ReactNode[]
    data: TYPE[]
    rowBuilder: (elem: TYPE) => ReactNode[]
    selectable: boolean
    onSelectionChanges?: (elem: TYPE | null) => void
    onClick?: (elem: TYPE) => void
    centered?: boolean
}

/**
 * Abstract table component
 * @param props
 * @constructor
 */
function Table<TYPE extends IDENTIFIABLE>(props: TableProps<TYPE>) {
    const [selection, setSelection] = useState<string | null>(null)

    function onSelect(elem: TYPE) {
        if (props.selectable) {
            setSelection(
                notNull(selection) && selection === elem.id ? null : elem.id
            )
        }
        if (props.onClick) props.onClick(elem)
    }

    useEffect(() => {
        if (props.onSelectionChanges) {
            if (notNull(selection)) {
                props.onSelectionChanges(
                    props.data.find((elem) => elem.id === selection)!
                )
            } else {
                props.onSelectionChanges(null)
            }
        }
    }, [selection])

    return (
        <MobileAlternative>
            <div className={"flex flex-col w-full space-y-4"}>
                {props.data.map((elem) => (
                    <div
                        key={elem.id}
                        onClick={() => onSelect(elem)}
                        className={
                            "flex flex-col space-y-4 rounded cursor-pointer border-2 p-4 " +
                            (notNull(selection) && selection === elem.id
                                ? "border-blue-600 bg-blue-600 bg-opacity-20"
                                : "border-transparent")
                        }>
                        {props.rowBuilder(elem).map((node, index) => (
                            <div
                                className={"flex flex-col"}
                                key={`${elem.id}.${index}`}>
                                <div className={"font-bold"}>
                                    {props.header[index]}
                                </div>
                                <div>{node}</div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
            <div className={"flex flex-col w-full"}>
                <div className={"flex p-4"}>
                    {props.header.map((h, index) => (
                        <div
                            key={`header.${index}`}
                            className={
                                "flex-1 font-bold overflow-x-hidden flex " +
                                (props.centered !== undefined && props.centered
                                    ? "justify-center"
                                    : "")
                            }>
                            {h}
                        </div>
                    ))}
                </div>
                <div className={"flex flex-col"}>
                    {props.data.map((elem) => (
                        <div
                            key={elem.id}
                            onClick={() => onSelect(elem)}
                            className={
                                "flex rounded cursor-pointer border-2 p-4 " +
                                (notNull(selection) && selection === elem.id
                                    ? "border-blue-600 bg-blue-600 bg-opacity-20"
                                    : "border-transparent hover:bg-neutral-100 hover:border-neutral-300")
                            }>
                            {props.rowBuilder(elem).map((node, index) => (
                                <div
                                    key={`${elem.id}.${index}`}
                                    className={
                                        "flex-1 flex overflow-x-hidden " +
                                        (props.centered !== undefined &&
                                        props.centered
                                            ? "justify-center"
                                            : "")
                                    }>
                                    {node}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </MobileAlternative>
    )
}

export default Table
